/**
 * SurveyGate — an unskippable popup shown to a company OWNER when Super Admin has
 * an active survey they haven't answered. Blocks the whole app (no close, no Esc,
 * no click-outside) until submitted. Renders nothing for non-owners, when there's
 * no pending survey, or on public/auth pages.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { loadPendingSurvey, submitSurvey } from '../utils/surveyHelper';

const SurveyGate = () => {
  const { currentUser, activeCompany } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!currentUser?.uid || !activeCompany?.id) { setSurvey(null); return; }
    loadPendingSurvey(activeCompany, currentUser.uid)
      .then((s) => { if (!cancelled) { setSurvey(s); setAnswers({}); setDone(false); } })
      .catch(() => { if (!cancelled) setSurvey(null); });
    return () => { cancelled = true; };
  }, [currentUser, activeCompany]);

  const setAnswer = (fid, val) => setAnswers((prev) => ({ ...prev, [fid]: val }));

  const handleSubmit = useCallback(async () => {
    if (!survey) return;
    // Validate required fields.
    for (const f of survey.fields) {
      if (f.required && !String(answers[f.id] ?? '').trim()) {
        setError(`Please answer: ${f.label}`);
        return;
      }
    }
    setError('');
    setSubmitting(true);
    try {
      await submitSurvey(survey, activeCompany, currentUser, answers);
      setDone(true);
      // Auto-dismiss the thank-you after a moment.
      setTimeout(() => setSurvey(null), 1800);
    } catch (e) {
      setError('Could not submit — please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }, [survey, answers, activeCompany, currentUser]);

  if (!survey) return null;

  return (
    <Overlay>
      <Card onClick={(e) => e.stopPropagation()}>
        {done ? (
          <ThankYou>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🙏</div>
            <h2>Thank you!</h2>
            <p>Your feedback helps us make AmalERP better.</p>
          </ThankYou>
        ) : (
          <>
            <Title>{survey.title || 'Quick question'}</Title>
            {survey.intro && <Intro>{survey.intro}</Intro>}
            <Fields>
              {survey.fields.map((f) => (
                <Field key={f.id}>
                  <Label>{f.label}{f.required && <Req> *</Req>}</Label>
                  {f.type === 'dropdown' ? (
                    <Select value={answers[f.id] || ''} onChange={(e) => setAnswer(f.id, e.target.value)}>
                      <option value="">-- Select --</option>
                      {(f.options || []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </Select>
                  ) : f.type === 'long' ? (
                    <TextArea rows={3} value={answers[f.id] || ''} onChange={(e) => setAnswer(f.id, e.target.value)} placeholder="Type your answer…" />
                  ) : (
                    <Input value={answers[f.id] || ''} onChange={(e) => setAnswer(f.id, e.target.value)} placeholder="Type your answer…" />
                  )}
                </Field>
              ))}
            </Fields>
            {error && <ErrorText>{error}</ErrorText>}
            <SubmitBtn onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit'}
            </SubmitBtn>
            <Note>This quick survey is required to continue.</Note>
          </>
        )}
      </Card>
    </Overlay>
  );
};

// ── Styles ──
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: rgba(15, 15, 20, 0.62);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  backdrop-filter: blur(2px);
`;
const Card = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 92vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.35);
  padding: 26px 24px;
`;
const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 800;
  color: #1A1A1A;
`;
const Intro = styled.p`
  margin: 0 0 18px;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;
const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #333;
`;
const Req = styled.span` color: #C62828; `;
const baseField = `
  padding: 10px 12px;
  border: 1px solid #d5d5d5;
  border-radius: 8px;
  font-size: 14px;
  color: #1A1A1A;
  width: 100%;
  &:focus { outline: none; border-color: #800020; }
`;
const Input = styled.input`${baseField}`;
const TextArea = styled.textarea`${baseField} resize: vertical;`;
const Select = styled.select`${baseField} background: #fff; cursor: pointer;`;
const ErrorText = styled.div`
  margin-top: 14px;
  color: #C62828;
  font-size: 13px;
  font-weight: 600;
`;
const SubmitBtn = styled.button`
  margin-top: 20px;
  width: 100%;
  padding: 12px;
  background: #800020;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  &:hover { opacity: 0.94; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const Note = styled.div`
  margin-top: 10px;
  text-align: center;
  font-size: 11px;
  color: #999;
`;
const ThankYou = styled.div`
  text-align: center;
  padding: 20px 10px;
  h2 { margin: 0 0 6px; font-size: 22px; color: #1A1A1A; }
  p { margin: 0; font-size: 14px; color: #666; }
`;

export default SurveyGate;
